import React, {Component, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import AssocManagementRepository from './AssocManagementRepository'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {Nullable} from '../../app/TypeHelpers'
import {OptionProps} from '../shared/forms/PPSelect'
import {AdminDefaultButton} from '../../styledComponents/adminPage/Buttons'
import {Information} from '../../network/response/Information'
import {AssocOfCondominium} from './model/AssocOfCondominium'
import {AssocTerm} from './model/AssocTerm'
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {DeleteLink, EditLink, StyledLink} from '../../styledComponents/adminPage/Links'
import {Messages} from '../../Messages'
import {HKConstants} from '../../HKConstants'
import AuthorityChecker from '../AuthorityChecker'
import {LeftRowFlexContainer} from '../../styledComponents/FlexComponents'
import styled, {css} from 'styled-components'
import {
    AssocItemName,
    convertAssocListToAssocSelectList,
    FitPageMainArea,
    formatTerm,
    TransparentSelect
} from './shared/AssocComponents'
import {LightGrayTableHead} from '../../styledComponents/adminPage/Table'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import {KEY_ASSOC_TERM_LIST_SEARCH_CONDITION} from '../../app/AdminSessionUtil'
import {SelectChangeEvent} from '@mui/material/Select'
import {Location} from 'react-router-dom'

interface SearchCondition {
    assocId: number
}

export interface AssocTermLocationState {
    information: Information
}

interface Props {
    location: Location<AssocTermLocationState>
    condominiumCd: string
    assocManagementRepo: AssocManagementRepository
    eventEmitter: EventEmitterExAdmin
}

interface State {
    assocList: AssocOfCondominium[]
    assocSelectList: OptionProps[]
    selectedAssocId: number
    assocTermList: AssocTerm[]
    isLoadComplete: boolean
}

class AdminAssocTermListPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        this.state = {
            assocList: [],
            assocSelectList: [],
            selectedAssocId: 0,
            assocTermList: [],
            isLoadComplete: false,
        }
    }

    async componentDidMount(): Promise<void> {
        const {
            location,
            assocManagementRepo,
            condominiumCd,
            eventEmitter,
        } = this.props

        const information = location.state?.information
        this.props.navigate(this.props.location.pathname, {replace: true})
        if (information) {
            await this.props.eventEmitter.showHeadingAreaMessage({
                title: information.title ?? '',
                messageLevel: information.type,
                detail: information.message,
            })
        }

        await eventEmitter.showLoading()

        try {
            const assocList: Nullable<AssocOfCondominium[]> =
                await this.props.asyncCallFunction(
                    assocManagementRepo.getAssocList.bind(assocManagementRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
            if (assocList === null) {
                return
            }
            if (assocList.length === 0) {
                this.props.navigate('/admin/error')
                return
            }

            const assocSelectList = convertAssocListToAssocSelectList(assocList)

            let selectedAssocId: number = assocList[0].assocId
            const beforeSelectedAssocId =
                SessionStorageHelper.getObject<SearchCondition>(KEY_ASSOC_TERM_LIST_SEARCH_CONDITION)?.assocId
            if (beforeSelectedAssocId !== undefined &&
                assocList.some(assoc => assoc.assocId === beforeSelectedAssocId)
            ) {
                selectedAssocId = beforeSelectedAssocId
            }

            this.setState({
                assocList: assocList,
                assocSelectList: assocSelectList,
                selectedAssocId: selectedAssocId,
            })
        } finally {
            await eventEmitter.closeLoading()
        }
    }

    async componentDidUpdate(prevProps: Props, prevState: State): Promise<void> {
        if (prevState.selectedAssocId !== this.state.selectedAssocId) {
            await this.props.eventEmitter.showLoading()

            try {
                const assocTerms = await this.getAssocTerms(this.props.condominiumCd, this.state.selectedAssocId)
                if (!assocTerms) {
                    return
                }

                this.setState({
                    assocTermList: assocTerms,
                    isLoadComplete: true,
                })
            } finally {
                await this.props.eventEmitter.closeLoading()
            }
        }
    }

    async getAssocTerms(condominiumCd: string, assocId: number): Promise<Nullable<AssocTerm[]>> {
        return await this.props.asyncCallFunction(this.props.assocManagementRepo.getAssocTerms.bind(
                this.props.assocManagementRepo,
                condominiumCd,
                assocId,
            ),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            },
        )
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    onChangeAssocListSelect = (event: SelectChangeEvent<unknown>) => {
        this.beforeUserAction()
        if (!event.target.value) {
            return
        }
        const value: number = Number(event.target.value)

        SessionStorageHelper.setObject<SearchCondition>(
            KEY_ASSOC_TERM_LIST_SEARCH_CONDITION,
            {
                assocId: value,
            },
        )

        this.setState({
            selectedAssocId: value,
        })
    }

    handleClickRegisterTermButton = () => {
        this.props.navigate(
            `/admin/condominiums/${this.props.condominiumCd}/assoc-terms/register`,
            {
                state: {
                    assocList: this.state.assocList,
                    selectedAssocId: this.state.selectedAssocId,
                },
            },
        )
    }

    handleClickTermNameButton = (assocTermId: number) => () => {
        this.props.navigate(
            `/admin/condominiums/${this.props.condominiumCd}/assoc-directors`,
            {
                state: {
                    initialDisplay: {
                        assocId: this.state.selectedAssocId,
                        assocTermId: assocTermId,
                    },
                },
            },
        )
    }

    handleClickEditTermButton = (assocTerm: AssocTerm) => () => {
        this.props.navigate(
            `/admin/condominiums/${this.props.condominiumCd}/assoc-terms/edit`,
            {
                state: {
                    assocList: this.state.assocList,
                    selectedAssocId: this.state.selectedAssocId,
                    baseAssocTermObject: assocTerm.toObject(),
                },
            },
        )
    }

    handleClickDeleteTermButton = (assocTermId: number) => () => {
        this.beforeUserAction()
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IASOA0003.errorLevel,
                message: Messages.IASOA0003.toString(),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: () => this.props.eventEmitter.closeDialog(),
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleClickOkOnAssocTermDeleteConfirmDialog(assocTermId),
                    },
                ],
            },
        )
    }

    handleClickOkOnAssocTermDeleteConfirmDialog = (assocTermId: number) => async () => {
        const {assocManagementRepo, condominiumCd, asyncCallFunction} = this.props
        const {selectedAssocId} = this.state

        this.props.eventEmitter.closeDialog()

        await this.props.eventEmitter.showLoading()
        try {
            const deleteMessage: Nullable<Information> = await asyncCallFunction(
                assocManagementRepo.deleteAssocTerm.bind(
                    assocManagementRepo,
                    condominiumCd,
                    selectedAssocId,
                    assocTermId,
                ),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (deleteMessage === null) {
                return
            }

            await this.props.eventEmitter.showHeadingAreaMessage({
                title: deleteMessage.title || '',
                messageLevel: deleteMessage.type,
                detail: deleteMessage.message,
            })

            const assocTerms = await this.getAssocTerms(condominiumCd, selectedAssocId)
            if (!assocTerms) {
                return
            }

            this.setState({
                assocTermList: assocTerms,
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }

    }

    renderAssocTermList(): ReactNode {
        const {assocTermList} = this.state

        return (
            <FitPageMainArea>
                <Table>
                    <LightGrayTableHead>
                        <TableRow>
                            <NameCell>任期の名前</NameCell>
                            <TermCell>期間</TermCell>
                            <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_ASSOC}>
                                <OperationCell/>
                            </AuthorityChecker>
                        </TableRow>
                    </LightGrayTableHead>
                    <TableBody>
                        {assocTermList.map(assocTerm =>
                            <TableRow key={`${assocTerm.assocTermId}`}>
                                <NameCell>
                                    <TermNameLink onClick={this.handleClickTermNameButton(assocTerm.assocTermId)}>
                                        {assocTerm.assocTermName}
                                    </TermNameLink>
                                </NameCell>
                                <TermCell>
                                    {formatTerm(assocTerm.assocTermStartDate, assocTerm.assocTermEndDate)}
                                </TermCell>
                                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_ASSOC}>
                                    <OperationCell>
                                        <EditLink onClick={this.handleClickEditTermButton(assocTerm)}>
                                            編集
                                        </EditLink>
                                        <Box display={'inline-block'} width={'24px'}/>
                                        <DeleteLink onClick={this.handleClickDeleteTermButton(assocTerm.assocTermId)}>
                                            削除
                                        </DeleteLink>
                                    </OperationCell>
                                </AuthorityChecker>
                            </TableRow>,
                        )}
                    </TableBody>
                </Table>
            </FitPageMainArea>
        )
    }

    render(): ReactNode {
        if (!this.state.isLoadComplete) {
            return null
        }
        return <>
            <Box height={'9px'}/>
            {this.state.assocSelectList.length >= 2 && <>
                <label>
                    <LeftRowFlexContainer>
                        <AssocItemName>管理組合</AssocItemName>
                        <TransparentSelect
                            data={this.state.assocSelectList}
                            value={this.state.selectedAssocId}
                            onChange={this.onChangeAssocListSelect}
                        />
                    </LeftRowFlexContainer>
                </label>
                <Box height={'16px'}/>
            </>}
            <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_ASSOC}>
                <RegisterButton
                    onClick={this.handleClickRegisterTermButton}
                >
                    任期を登録する
                </RegisterButton>
            </AuthorityChecker>
            <Box height={'16px'}/>
            {this.state.assocTermList.length === 0 && <>
                <Box height={'8px'}/>
                <div>理事会の任期が登録されていません。</div>
            </>}
            {this.state.assocTermList.length !== 0 &&
                this.renderAssocTermList()
            }
        </>
    }
}

const TableCellBaseCss = css`
  padding-top: 16px;
  padding-bottom: 16px;
`

const NameCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellBaseCss};
    padding-left: 40px;
    padding-right: 20px;
    min-width: 180px;
  }
`

const TermCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellBaseCss};
    padding-left: 20px;
    padding-right: 40px;
    white-space: nowrap;
    line-height: 24px;
  }
`

const OperationCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellBaseCss};
    padding-left: 80px;
    padding-right: 40px;
    white-space: nowrap;
  }
`

const TermNameLink = styled(StyledLink)`
  text-align: left;
`

const RegisterButton = styled(AdminDefaultButton)`
  width: 162px;
`

export default createAdminPage(AdminAssocTermListPage)
